import {
  FormCheckbox,
  FormDatePicker,
  FormFileInput,
  FormInput,
  FormSelect,
} from "@/components";
import { Button, Card } from "@/components/ui";
import { getBasePath, LOGIN_ROLES, yearOptions } from "@/lib/utils";
import AddNewInstallmentDrawer from "@/pages/SalesOps/School/Add/TabsData/InstallmentSchedule/EditComponents/AddNewInstallmentDrawer";
import { RootState } from "@/redux/store";
import { Bookmark, PencilIcon, EyeIcon, Trash2 } from "lucide-react";
import { FC } from "react";
import { FormProvider } from "react-hook-form";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { TabsDataTypes } from "../types";
import {
  AGREEMENT_DOCUMENT,
  AGREEMENT_DOCUMENT_FILE_NAME,
  CONTRACT_YEARS,
  DEAL_AMOUNT,
  DEAL_CLOSED_BY,
  DEAL_CLOSURE_DATE,
  EXPECTED_DUE_DATE,
  EXPECTED_LAUNCH_DATE,
  INCLUDES_GST,
  INSTALLMENT,
  INSTALLMENT_AMOUNT,
  INSTALLMENT_NUMBER,
  INSTALLMENT_YEAR,
  LEAD_TYPE,
  leadTypeOptions,
  PAYMENT_FROM_ENTITY,
  pdcOptions,
  PDCS_COLLECTED,
  SALES_PERSON_TYPE,
  MOU_AMOUNT,
  INTEREST_PERCENTAGE,
  INTEREST_AMOUNT,
  salesPersonTypeOptions,
  gstIncludesOptions,
} from "./consts";
import useInstallmentSchedule from "./useInstallmentSchedule";

const InstallmentSchedule: FC<TabsDataTypes> = ({ onNextClick }) => {
  const { action, id } = useParams();
  const navigate = useNavigate();
  const {
    form,
    form2,
    requiredFields,
    onSubmit,
    onSubmit2,
    onCancel,
    attachmentRef,
    onAddNewAcademicInfo,
    onRemoveAcademicInfo,
    scrollRef,
    isLoading,
    openAddInstallment,
    onCancelInstallment,
    hasRole,
    roles,
    dealClosedByOptions,
  } = useInstallmentSchedule({ onNextClick });
  const { errors } = form.formState;
  const { errors: errors2 } = form2.formState;
  const isEdit = action === "edit" && !!id;
  const isView = action === "view" && !!id;
  const loggedInUserData = useSelector(
    (state: RootState) => state.currentData.loggedInUserData
  );

  const basePath = getBasePath(loggedInUserData?.role);

  const restrictedRoles = [LOGIN_ROLES.INVENTORY_TEAM, LOGIN_ROLES.LABS_TEAM];
  const canShowActionButton = !restrictedRoles.some(role => hasRole(role));
  
  return (
    <div className="px-2">
      {isLoading && <div className="py-2 text-white">Loading...</div>}

      {!isLoading && (
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="relative">
            <div className="bg-white rounded-lg pt-3 pb-[100px] px-5">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-[16px] text-primary mb-5">
                  Commercial Info
                </h2>
                {(
                  hasRole(LOGIN_ROLES.SUPER_ADMIN)) || hasRole(LOGIN_ROLES.SALES_OPS) && (
                  <Button type="submit" variant="default">
                    Save
                  </Button>
                )}
              </div>
              <fieldset
                disabled={
                  !(
                    hasRole(LOGIN_ROLES.SUPER_ADMIN) || hasRole(LOGIN_ROLES.SALES_OPS)
                  )
                }
              >
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[25px]">
                  <FormDatePicker
                    name={DEAL_CLOSURE_DATE}
                    control={form.control}
                    label="Deal Closure Date"
                    placeholder="Please select"
                    error={errors[DEAL_CLOSURE_DATE]?.message}
                    isRequired={requiredFields.includes(DEAL_CLOSURE_DATE)}
                    disabled={hasRole(LOGIN_ROLES.CRM_HEAD)}
                  />
                  <FormInput
                    name={DEAL_AMOUNT}
                    control={form.control}
                    label="Deal Amount"
                    placeholder="Please enter"
                    error={errors[DEAL_AMOUNT]?.message}
                    isRequired={requiredFields.includes(DEAL_AMOUNT)}
                    variant="integer"
                  />
                  <FormSelect
                    name={INCLUDES_GST}
                    control={form.control}
                    label="Includes GST?"
                    error={errors[INCLUDES_GST]?.message}
                    isRequired={requiredFields.includes(INCLUDES_GST)}
                    options={gstIncludesOptions}
                  />
                  <FormSelect
                    name={PDCS_COLLECTED}
                    control={form.control}
                    label="PDCs Collected?"
                    placeholder="Please Select"
                    error={errors[PDCS_COLLECTED]?.message}
                    isRequired={requiredFields.includes(PDCS_COLLECTED)}
                    options={pdcOptions}
                  />
                  <FormSelect
                    name={SALES_PERSON_TYPE}
                    control={form.control}
                    label="Sales Person Type"
                    placeholder="Please Select"
                    error={errors[SALES_PERSON_TYPE]?.message}
                    isRequired={requiredFields.includes(SALES_PERSON_TYPE)}
                    options={salesPersonTypeOptions}
                  />
                  <FormSelect
                    name={DEAL_CLOSED_BY}
                    control={form.control}
                    label="Deal Closed By"
                    placeholder="Please Select"
                    error={errors[DEAL_CLOSED_BY]?.message}
                    isRequired={requiredFields.includes(DEAL_CLOSED_BY)}
                    options={dealClosedByOptions}
                  />
                  <FormSelect
                    name={LEAD_TYPE}
                    control={form.control}
                    label="Lead Type"
                    placeholder="Please Select"
                    error={errors[LEAD_TYPE]?.message}
                    isRequired={requiredFields.includes(LEAD_TYPE)}
                    options={leadTypeOptions}
                  />
                  <FormInput
                    name={CONTRACT_YEARS}
                    control={form.control}
                    label="Contract Years"
                    placeholder="Please enter"
                    error={errors[CONTRACT_YEARS]?.message}
                    isRequired={requiredFields.includes(CONTRACT_YEARS)}
                  />
                  <FormInput
                    name={PAYMENT_FROM_ENTITY}
                    control={form.control}
                    label="Payment From Entity"
                    placeholder="Please enter"
                    error={errors[PAYMENT_FROM_ENTITY]?.message}
                    isRequired={requiredFields.includes(PAYMENT_FROM_ENTITY)}
                  />
                  <FormDatePicker
                    name={EXPECTED_LAUNCH_DATE}
                    control={form.control}
                    label="Expected Launch Date"
                    placeholder="Please select"
                    error={errors[EXPECTED_LAUNCH_DATE]?.message}
                    isRequired={requiredFields.includes(EXPECTED_LAUNCH_DATE)}
                  />
                  <FormFileInput
                    fileInputRef={attachmentRef}
                    name={AGREEMENT_DOCUMENT}
                    // fileName={form.watch(AGREEMENT_DOCUMENT_FILE_NAME)}
                    control={form.control}
                    label="Agreement Document"
                    error={errors[AGREEMENT_DOCUMENT]?.message}
                    isRequired={requiredFields.includes(AGREEMENT_DOCUMENT)}
                    maxSize={5} // in MB
                    accept="application/pdf"
                  />
                </div>
              </fieldset>
            </div>
          </form>
          <Card className="p-5 pt-0 mt-6 shadow-none">
            <form onSubmit={form2.handleSubmit(onSubmit2)} className="relative">
              <>
                <div className="flex flex-col items-start md:flex-row md:items-center justify-between mb-4 md:mb-auto">
                  <h2 className="text-[16px] text-primary my-5">
                    Academic and Operational Info
                  </h2>
                  <div className="flex items-center gap-4">
                    {form.watch(INCLUDES_GST) == "Included" && (
                      <div className="bg-orange-100 rounded-lg px-4 py-3 text-center">
                        <span className="font-bold text-primary">
                          Deal Amount : ₹{" "}
                          {Number(form.watch(DEAL_AMOUNT) || "").toLocaleString(
                            "en-IN"
                          )}{" "}
                          Included GST
                        </span>
                      </div>
                    )}

                    {isEdit &&
                      (
                        hasRole(LOGIN_ROLES.SUPER_ADMIN)) && (
                        <Button type="submit" variant="default">
                          Save
                        </Button>
                      )}


                    {(
                      hasRole(LOGIN_ROLES.SUPER_ADMIN)) && (
                      <Button type="button" onClick={onAddNewAcademicInfo}>
                        + Add New
                      </Button>
                      )} 
                  </div>
                </div>

                <div
                  ref={scrollRef}
                  className="grid grid-cols-1 mt-10 gap-[20px] overflow-y-scroll h-[260px]"
                >
                  {form2.watch(INSTALLMENT).map((installment, index) => {
                    return (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-7 gap-[30px]">
                        <FormSelect
                          name={`${INSTALLMENT}.${index}.${INSTALLMENT_YEAR}`}
                          control={form2.control}
                          label="Year"
                          placeholder="Please select"
                          error={
                            (errors2[INSTALLMENT] as any)?.[`${index}`]?.[
                              INSTALLMENT_YEAR
                            ]?.message
                          }
                          isRequired={requiredFields.includes(
                            `${INSTALLMENT}[].${INSTALLMENT_YEAR}`
                          )}
                          options={yearOptions()}
                          disabled={
                            !(
                              hasRole(LOGIN_ROLES.SUPER_ADMIN)
                            )
                          }
                        />
                        <FormInput
                          name={`${INSTALLMENT}.${index}.${INSTALLMENT_NUMBER}`}
                          control={form2.control}
                          label="Installment #"
                          placeholder="Please enter"
                          error={
                            (errors2[INSTALLMENT] as any)?.[`${index}`]?.[
                              INSTALLMENT_NUMBER
                            ]?.message
                          }
                          isRequired={requiredFields.includes(
                            `${INSTALLMENT}[].${INSTALLMENT_NUMBER}`
                          )}
                          disabled={
                            !(
                              hasRole(LOGIN_ROLES.SUPER_ADMIN)
                            )
                          }
                        />
                        <FormInput
                          name={`${INSTALLMENT}.${index}.${MOU_AMOUNT}`}
                          control={form2.control}
                          label="MOU Amount"
                          placeholder="Please enter"
                          prefix="₹"
                          error={
                            (errors2[INSTALLMENT] as any)?.[`${index}`]?.[
                              MOU_AMOUNT
                            ]?.message
                          }
                          isRequired={requiredFields.includes(
                            `${INSTALLMENT}[].${MOU_AMOUNT}`
                          )}
                          disabled={
                            !(
                              hasRole(LOGIN_ROLES.SUPER_ADMIN)
                            )
                          }
                        />
                        <FormInput
                          name={`${INSTALLMENT}.${index}.${INTEREST_PERCENTAGE}`}
                          control={form2.control}
                          label="Installment Interest %"
                          placeholder="Please enter"
                          error={
                            (errors2[INSTALLMENT] as any)?.[`${index}`]?.[
                              INTEREST_PERCENTAGE
                            ]?.message
                          }
                          isRequired={requiredFields.includes(
                            `${INSTALLMENT}[].${INTEREST_PERCENTAGE}`
                          )}
                          disabled={
                            !(
                              hasRole(LOGIN_ROLES.SUPER_ADMIN)
                            )
                          }
                        />

                        <FormDatePicker
                          name={`${INSTALLMENT}.${index}.${EXPECTED_DUE_DATE}`}
                          control={form2.control}
                          label="Expected Due Date"
                          placeholder="Please select"
                          error={
                            (errors2[INSTALLMENT] as any)?.[`${index}`]?.[
                              EXPECTED_DUE_DATE
                            ]?.message
                          }
                          isRequired={requiredFields.includes(
                            `${INSTALLMENT}[].${EXPECTED_DUE_DATE}`
                          )}
                          disabled={
                            !(
                              hasRole(LOGIN_ROLES.SUPER_ADMIN)
                            )
                          }
                        />
                        <FormInput
                          name={`${INSTALLMENT}.${index}.${INSTALLMENT_AMOUNT}`}
                          control={form2.control}
                          label="Installment Amount"
                          placeholder="Please enter"
                          prefix="₹"
                          error={
                            (errors2[INSTALLMENT] as any)?.[`${index}`]?.[
                              INSTALLMENT_AMOUNT
                            ]?.message
                          }
                          isRequired={requiredFields.includes(
                            `${INSTALLMENT}[].${INSTALLMENT_AMOUNT}`
                          )}
                          disabled={
                            !(
                              hasRole(LOGIN_ROLES.SUPER_ADMIN)
                            )
                          }
                        />
                        <div className="grid grid-cols-2 items-start mx-3">
                          <div className="flex items-center justify-center my-5">
                            {isEdit &&
                              (hasRole("SUPER_ADMIN") ||
                                hasRole("FINANCE_TEAM")) && (
                                <PencilIcon
                                  className="stroke-[rgba(70,81,98,1)] cursor-pointer m-3"
                                  onClick={() => {
                                    navigate(`installment/${installment._id}`);
                                  }}
                                />
                              )}
                            {isEdit &&
                              hasRole("CRM_HEAD") || hasRole(LOGIN_ROLES.SALES_OPS) && (
                              <EyeIcon
                                  className="stroke-[rgba(70,81,98,1)] cursor-pointer m-3"
                                  onClick={() => {
                                    navigate(`installment/${installment._id}`);
                                  }}
                                />
                              )}
                            {
                              index !== 0 &&
                              (
                                hasRole(LOGIN_ROLES.SUPER_ADMIN)) && (
                                <Trash2
                                  className="stroke-red-600 h-5 cursor-pointer"
                                  onClick={() => onRemoveAcademicInfo(index)}
                                />
                              )}

                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
              {/* </div> */}
            </form>
          </Card>
        </FormProvider>
      )}
      {openAddInstallment && (
        <AddNewInstallmentDrawer
          onCancel={onCancelInstallment}
          open={openAddInstallment}
        />
      )}
    </div>
  );
};

export default InstallmentSchedule;
